import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AUDIT_ACTION_LABELS,
  type AuditEventResponse,
  type EmployeeResponse,
  employmentLabel,
  formatIban,
  formatMoney,
  type PeopleOptions,
  SALARY_MONTHS,
  SALARY_PAYMENT_KIND_LABELS,
  SALARY_PAYMENT_KINDS,
  salaryMonthLabel,
  type SalaryMonthResponse,
  type SalaryPaymentKind,
  type SalaryPaymentRequest,
  type SalaryYearResponse,
  TFR_DESTINATION_LABELS,
} from '@k-management/shared';
import { Fragment, type ReactNode, useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { useCan } from '../../auth/AuthProvider.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { DecimalInput } from '../../components/DecimalInput.js';
import { Field } from '../../components/Field.js';
import { type Command, useCommands, usePageTitle } from '../../components/layout/page-chrome.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate, formatTimestamp } from '../../lib/format.js';
import { EmployeeDialog } from './EmployeeDialog.js';
import { CollaboratorRates } from './CollaboratorRates.js';
import { EmployeeCost } from './EmployeeCost.js';
import { EmployeeTfr } from './EmployeeTfr.js';
import { reconcileWindowPath } from '../treasury/ReconcilePanel.js';
import { maskedIban, moneyOf, peopleKeys, SalaryStatePill } from './people-ui.js';

type Tab = 'dati' | 'stipendi' | 'tfr' | 'costo' | 'storico';
const ALL_TABS: [Tab, string][] = [
  ['dati', 'Dati'],
  ['stipendi', 'Stipendi'],
  ['tfr', 'TFR'],
  ['costo', 'Costo aziendale'],
  ['storico', 'Storico'],
];
/** A collaborator has no payslips: data, the typed cost, the history (2.38). */
const COLLABORATOR_TABS: Tab[] = ['dati', 'costo', 'storico'];

/** The card of an employee (07/10/2026): data, the salaries of the year, the TFR, the history. */
export function EmployeePage() {
  const { id = '' } = useParams();
  const [search] = useSearchParams();
  const canWrite = useCan('persone.write');
  // The company cost only with «persone.costs» (2.37).
  const canSeeCosts = useCan('persone.costs');
  // «?sezione=tfr» opens a section, as from a bank movement (1.68).
  const [tab, setTab] = useState<Tab>(() => {
    const asked = search.get('sezione');
    return ALL_TABS.some(([key]) => key === asked) ? (asked as Tab) : 'dati';
  });
  const [editing, setEditing] = useState(false);
  const employee = useQuery({
    queryKey: peopleKeys.employee(id),
    queryFn: () => apiRequest<EmployeeResponse>(`/people/employees/${id}`),
  });
  const data = employee.data;
  const collaborator = data?.relationship === 'collaboratore';
  const TABS = ALL_TABS.filter(
    ([key]) =>
      (key !== 'costo' || canSeeCosts) && (!collaborator || COLLABORATOR_TABS.includes(key)),
  ).map(([key, label]): [Tab, string] =>
    key === 'costo' && collaborator ? [key, 'Costo'] : [key, label],
  );
  // A section the person does not have (the salaries of a collaborator) falls back on the data.
  const shown: Tab = TABS.some(([key]) => key === tab) ? tab : 'dati';
  usePageTitle(data ? `${data.lastName} ${data.firstName}` : 'Dipendente');
  useCommands(
    canWrite && data && shown === 'dati'
      ? [
          {
            key: 'edit',
            label: 'Modifica',
            icon: 'edit',
            primary: true,
            onClick: () => setEditing(true),
          } satisfies Command,
        ]
      : [],
  );

  return (
    <section className="stack">
      <div className="page-header">
        <div>
          <p className="muted small">
            <Link to="/organizzazione/persone">Persone</Link> › {data?.lastName ?? '…'}
          </p>
          <h1>{data ? `${data.lastName} ${data.firstName}` : 'Dipendente'}</h1>
          {data && (
            <p className="muted">
              {employmentLabel(data)}
              {data.roleName && ` · ${data.roleName}`} · dal {formatDate(data.hiredOn)}
              {data.leftOn && `, cessato il ${formatDate(data.leftOn)}`}
            </p>
          )}
        </div>
      </div>
      {employee.isError && <p className="error">{employee.error.message}</p>}
      {data && (
        <>
          <nav className="subtabs" aria-label="Sezioni della persona">
            {TABS.map(([key, label]) => (
              <button
                key={key}
                type="button"
                className={shown === key ? 'subtab selected' : 'subtab'}
                onClick={() => setTab(key)}
              >
                {label}
              </button>
            ))}
          </nav>
          {shown === 'dati' && <Details employee={data} />}
          {shown === 'stipendi' && <Salaries employee={data} />}
          {shown === 'tfr' && <EmployeeTfr employee={data} />}
          {shown === 'costo' &&
            canSeeCosts &&
            (data.relationship === 'collaboratore' ? (
              <CollaboratorRates employee={data} />
            ) : (
              <EmployeeCost employee={data} />
            ))}
          {shown === 'storico' && <History id={data.id} />}
        </>
      )}
      {editing && data && <EmployeeDialog employee={data} onClose={() => setEditing(false)} />}
    </section>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{children || '—'}</dd>
    </div>
  );
}

function Details({ employee }: { employee: EmployeeResponse }) {
  const [showIban, setShowIban] = useState(false);
  return (
    <div className="employee-cards">
      <div className="card">
        <h3>Anagrafica</h3>
        <dl className="facts">
          <Row label="Codice fiscale">{employee.taxCode ?? '—'}</Row>
          <Row label="Nascita">
            {[employee.birthDate && formatDate(employee.birthDate), employee.birthPlace]
              .filter(Boolean)
              .join(' · ')}
          </Row>
          <Row label="Residenza">{employee.address}</Row>
          <Row label="Telefono">{employee.phone}</Row>
          <Row label="E-mail personale">{employee.personalEmail}</Row>
          <Row label="E-mail aziendale">{employee.workEmail}</Row>
        </dl>
      </div>
      {employee.relationship === 'collaboratore' ? (
        <div className="card">
          <h3>Collaborazione</h3>
          <dl className="facts">
            <Row label="Rapporto">{employmentLabel(employee)}</Row>
            <Row label="Fornitore in Anagrafica">
              {employee.subjectId && (
                <Link to={`/anagrafica/${employee.subjectId}`}>{employee.subjectName}</Link>
              )}
            </Row>
            <Row label="Mansione">{employee.jobTitle}</Row>
            <Row label="Ruolo aziendale">{employee.roleName}</Row>
            <Row label="Inizio">{formatDate(employee.hiredOn)}</Row>
            <Row label="Fine">{employee.leftOn && formatDate(employee.leftOn)}</Row>
            <Row label="Note">{employee.notes}</Row>
          </dl>
        </div>
      ) : (
        <>
          <div className="card">
            <h3>Rapporto di lavoro</h3>
            <dl className="facts">
              <Row label="Contratto">{employmentLabel(employee)}</Row>
              <Row label="CCNL e livello">
                {[employee.ccnl, employee.level && `livello ${employee.level}`]
                  .filter(Boolean)
                  .join(' · ')}
              </Row>
              <Row label="Mansione">{employee.jobTitle}</Row>
              <Row label="Ruolo aziendale">{employee.roleName}</Row>
              <Row label="Assunzione">{formatDate(employee.hiredOn)}</Row>
              <Row label="Cessazione">{employee.leftOn && formatDate(employee.leftOn)}</Row>
              <Row label="Mensilità">13 (12 e la tredicesima)</Row>
            </dl>
          </div>
          <div className="card">
            <h3>Pagamenti e TFR</h3>
            <dl className="facts">
              <Row label="IBAN stipendio">
                {employee.iban && (
                  <>
                    {showIban ? formatIban(employee.iban) : maskedIban(employee.iban)}{' '}
                    <button type="button" className="link" onClick={() => setShowIban(!showIban)}>
                      {showIban ? 'Nascondi' : 'Mostra'}
                    </button>
                  </>
                )}
              </Row>
              <Row label="Pagato dal conto">{employee.payingAccountName}</Row>
              <Row label="Destinazione TFR">
                {employee.tfr && TFR_DESTINATION_LABELS[employee.tfr]}
              </Row>
              {employee.tfr === 'fondo' && (
                <>
                  <Row label="Fondo">{employee.pensionFundName}</Row>
                  <Row label="Adesione dal">
                    {employee.fundJoinedOn && formatDate(employee.fundJoinedOn)}
                  </Row>
                </>
              )}
              <Row label="Note">{employee.notes}</Row>
            </dl>
          </div>
        </>
      )}
    </div>
  );
}

/** The 13 salaries of a year: the net of each, the advances, the payments, what is left. */
function Salaries({ employee }: { employee: EmployeeResponse }) {
  const queryClient = useQueryClient();
  const canWrite = useCan('persone.write');
  const canTreasury = useCan('tesoreria.read');
  const [year, setYear] = useState(new Date().getFullYear());
  const [netOf, setNetOf] = useState<SalaryMonthResponse | null>(null);
  const [paying, setPaying] = useState<{ month: number; kind: SalaryPaymentKind } | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const salaries = useQuery({
    queryKey: peopleKeys.salaries(employee.id, year),
    queryFn: () =>
      apiRequest<SalaryYearResponse>(`/people/employees/${employee.id}/salaries?year=${year}`),
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: peopleKeys.all });
  const remove = useMutation({
    mutationFn: (paymentId: string) =>
      apiRequest(`/people/payments/${paymentId}`, { method: 'DELETE' }),
    onSuccess: async () => {
      setRemoving(null);
      await refresh();
    },
  });
  const first = Number(employee.hiredOn.slice(0, 4));
  const current = new Date().getFullYear();
  const years: number[] = [];
  for (let item = Math.max(current + 1, year); item >= Math.min(first, year); item -= 1)
    years.push(item);
  const money = (value: string | null) => (value === null ? '—' : formatMoney(value));

  return (
    <div className="stack">
      <div className="row between">
        <label className="row small">
          Anno{' '}
          <select value={year} onChange={(event) => setYear(Number(event.target.value))}>
            {years.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
        </label>
        {canWrite && (
          <button type="button" onClick={() => setPaying({ month: 0, kind: 'acconto' })}>
            + Acconto
          </button>
        )}
      </div>
      {salaries.isError && <p className="error">{salaries.error.message}</p>}
      {salaries.data && (
        <div className="table-scroll">
          <table className="data-table salary-table">
            <thead>
              <tr>
                <th>Mensilità</th>
                <th className="numeric">Netto in busta</th>
                <th className="numeric">Acconti</th>
                <th className="numeric">Saldo pagato</th>
                <th className="numeric">Resta</th>
                <th>Pagato il</th>
                <th>Stato</th>
                {canWrite && <th />}
              </tr>
            </thead>
            <tbody>
              {salaries.data.months.map((item) => (
                <Fragment key={item.month}>
                  <tr className={item.state === 'vuota' ? 'muted' : undefined}>
                    <td>{salaryMonthLabel(item.month)}</td>
                    <td className="numeric">
                      {canWrite ? (
                        <button type="button" className="link" onClick={() => setNetOf(item)}>
                          {item.net === null ? 'carica' : formatMoney(item.net)}
                        </button>
                      ) : (
                        money(item.net)
                      )}
                    </td>
                    <td className="numeric">
                      {Number(item.advances) ? formatMoney(item.advances) : '—'}
                    </td>
                    <td className="numeric">{Number(item.paid) ? formatMoney(item.paid) : '—'}</td>
                    <td className="numeric">{money(item.residual)}</td>
                    <td>{item.paidOn ? formatDate(item.paidOn) : '—'}</td>
                    <td>
                      <SalaryStatePill state={item.state} />
                    </td>
                    {canWrite && (
                      <td>
                        {item.net !== null && item.state !== 'pagato' && (
                          <button
                            type="button"
                            className="link"
                            onClick={() => setPaying({ month: item.month, kind: 'saldo' })}
                          >
                            Pagamento
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                  {item.payments.map((payment) => (
                    <tr key={payment.id} className="sub-row">
                      <td colSpan={canWrite ? 7 : 7}>
                        ↳ {SALARY_PAYMENT_KIND_LABELS[payment.kind].toLowerCase()} del{' '}
                        {formatDate(payment.date)} · {formatMoney(payment.amount)}
                        {payment.accountCode && ` · conto ${payment.accountCode}`}
                        {payment.movementId ? (
                          <>
                            {' · '}
                            {/* Opens the movement, to undo a wrong match (1.69). */}
                            {canTreasury && payment.accountCode ? (
                              <Link
                                to={reconcileWindowPath(payment.accountCode, payment.movementId)}
                                target="_blank"
                              >
                                dal movimento bancario
                              </Link>
                            ) : (
                              'dal movimento bancario'
                            )}
                          </>
                        ) : (
                          payment.note && ` · ${payment.note}`
                        )}
                      </td>
                      {canWrite && (
                        <td>
                          {!payment.movementId && (
                            <button
                              type="button"
                              className="link"
                              onClick={() => setRemoving(payment.id)}
                            >
                              Togli
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th>Totale {year}</th>
                <th className="numeric">{formatMoney(salaries.data.totals.net)}</th>
                <th className="numeric">{formatMoney(salaries.data.totals.advances)}</th>
                <th className="numeric">{formatMoney(salaries.data.totals.paid)}</th>
                <th className="numeric">{formatMoney(salaries.data.totals.residual)}</th>
                <th colSpan={canWrite ? 3 : 2} />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      <p className="muted small">
        Il netto si carica dalla busta paga, mese per mese. Un acconto si può segnare anche prima di
        caricare il mese: quando carichi il netto, «Resta» ne tiene già conto.
      </p>
      {netOf && (
        <NetDialog
          employeeId={employee.id}
          year={year}
          salary={netOf}
          onClose={() => setNetOf(null)}
        />
      )}
      {paying && (
        <PaymentDialog
          employee={employee}
          year={year}
          month={paying.month}
          kind={paying.kind}
          residual={
            salaries.data?.months.find((item) => item.month === paying.month)?.residual ?? null
          }
          onClose={() => setPaying(null)}
        />
      )}
      {removing && (
        <ConfirmDialog
          title="Togliere questo pagamento?"
          confirmLabel="Togli"
          busy={remove.isPending}
          error={remove.error?.message}
          onConfirm={() => remove.mutate(removing)}
          onCancel={() => setRemoving(null)}
        >
          <p>Resta nel registro attività.</p>
        </ConfirmDialog>
      )}
    </div>
  );
}

function NetDialog({
  employeeId,
  year,
  salary,
  onClose,
}: {
  employeeId: string;
  year: number;
  salary: SalaryMonthResponse;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [net, setNet] = useState(salary.net ?? '');
  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);
  const save = useMutation({
    mutationFn: (value: string | null) =>
      apiRequest(`/people/employees/${employeeId}/salaries/${year}/${salary.month}`, {
        method: 'PUT',
        body: { net: value },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: peopleKeys.all });
      onClose();
    },
  });
  return (
    <dialog ref={dialogRef} className="dialog narrow" onClose={onClose}>
      <div className="stack">
        <h2>
          {salaryMonthLabel(salary.month)} {year}
        </h2>
        <Field label="Netto in busta">
          <DecimalInput value={net} onChange={setNet} decimals={[2, 2]} autoFocus />
        </Field>
        {save.error && <p className="error">{save.error.message}</p>}
        <div className="actions">
          {salary.net !== null && (
            <button type="button" onClick={() => save.mutate(null)} disabled={save.isPending}>
              Togli il netto
            </button>
          )}
          <button type="button" onClick={onClose} disabled={save.isPending}>
            Annulla
          </button>
          <button
            type="button"
            className="primary"
            disabled={save.isPending || moneyOf(net) === null}
            onClick={() => save.mutate(moneyOf(net))}
          >
            Salva
          </button>
        </div>
      </div>
    </dialog>
  );
}

function PaymentDialog({
  employee,
  year,
  month,
  kind,
  residual,
  onClose,
}: {
  employee: EmployeeResponse;
  year: number;
  /** 0: to choose. */
  month: number;
  kind: SalaryPaymentKind;
  residual: string | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const options = useQuery({
    queryKey: peopleKeys.options,
    queryFn: () => apiRequest<PeopleOptions>('/people/options'),
  });
  const [form, setForm] = useState({
    month: month || new Date().getMonth() + 1,
    kind,
    date: new Date().toISOString().slice(0, 10),
    amount: kind === 'saldo' && residual && Number(residual) > 0 ? residual : '',
    accountCode: employee.payingAccountCode ?? '',
    note: '',
  });
  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);
  const save = useMutation({
    mutationFn: () => {
      const body: SalaryPaymentRequest = {
        kind: form.kind,
        date: form.date,
        amount: moneyOf(form.amount) ?? '',
        accountCode: form.accountCode || null,
        note: form.note,
      };
      return apiRequest(
        `/people/employees/${employee.id}/salaries/${year}/${form.month}/payments`,
        { method: 'POST', body },
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: peopleKeys.all });
      onClose();
    },
  });
  return (
    <dialog ref={dialogRef} className="dialog narrow" onClose={onClose}>
      <div className="stack">
        <h2>
          {form.kind === 'acconto' ? 'Acconto' : 'Pagamento'} · {employee.lastName}{' '}
          {employee.firstName}
        </h2>
        <div className="form-grid">
          <Field label="Mensilità">
            <select
              value={form.month}
              onChange={(event) => setForm({ ...form, month: Number(event.target.value) })}
            >
              {SALARY_MONTHS.map((item) => (
                <option key={item} value={item}>
                  {salaryMonthLabel(item)} {year}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Tipo">
            <select
              value={form.kind}
              onChange={(event) =>
                setForm({ ...form, kind: event.target.value as SalaryPaymentKind })
              }
            >
              {SALARY_PAYMENT_KINDS.map((item) => (
                <option key={item} value={item}>
                  {SALARY_PAYMENT_KIND_LABELS[item]}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Data">
            <input
              type="date"
              value={form.date}
              onChange={(event) => setForm({ ...form, date: event.target.value })}
            />
          </Field>
          <Field label="Importo">
            <DecimalInput
              value={form.amount}
              onChange={(value) => setForm({ ...form, amount: value })}
              decimals={[2, 2]}
            />
          </Field>
          <Field label="Conto">
            <select
              value={form.accountCode}
              onChange={(event) => setForm({ ...form, accountCode: event.target.value })}
            >
              <option value="">—</option>
              {options.data?.accounts.map((item) => (
                <option key={item.code} value={item.code}>
                  {item.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Nota">
            <input
              value={form.note}
              maxLength={300}
              onChange={(event) => setForm({ ...form, note: event.target.value })}
            />
          </Field>
        </div>
        {save.error && <p className="error">{save.error.message}</p>}
        <div className="actions">
          <button type="button" onClick={onClose} disabled={save.isPending}>
            Annulla
          </button>
          <button
            type="button"
            className="primary"
            disabled={save.isPending || moneyOf(form.amount) === null || !form.date}
            onClick={() => save.mutate()}
          >
            Salva
          </button>
        </div>
      </div>
    </dialog>
  );
}

function History({ id }: { id: string }) {
  const history = useQuery({
    queryKey: peopleKeys.history(id),
    queryFn: () => apiRequest<AuditEventResponse[]>(`/people/employees/${id}/history`),
  });
  return (
    <div className="card stack">
      <p className="muted small">
        Le modifiche, senza importi né dati di contatto: il registro attività lo leggono gli
        amministratori.
      </p>
      {history.data?.length === 0 && <p className="muted">Nessuna modifica.</p>}
      {history.data && history.data.length > 0 && (
        <ul className="plain-list">
          {history.data.map((event) => (
            <li key={event.id}>
              <span className="muted small">
                {formatTimestamp(event.occurredAt)} · {event.actorEmail ?? 'sistema'} ·{' '}
                {AUDIT_ACTION_LABELS[event.action]}
              </span>
              <div>{event.summary}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
